/**
 * Badge Category Form Dialog Component
 * Dialog for creating and editing badge categories
 */

import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Box,
  IconButton,
  Alert,
  Typography,
  Grid,
  CircularProgress,
  Chip,
  Popover,
  Paper,
} from '@mui/material'
import {
  Close as CloseIcon,
  EmojiEmotions as EmojiIcon,
  Lock as LockIcon,
} from '@mui/icons-material'
import EmojiPicker, { EmojiClickData, Theme, EmojiStyle } from 'emoji-picker-react'
import { useMobileDialogProps } from '@components/common/useMobileDialogProps'
import {
  useCreateBadgeCategory,
  useUpdateBadgeCategory,
} from '../../hooks/useBadgeCategories'
import type {
  BadgeCategoryDto,
  CreateBadgeCategoryRequest,
  UpdateBadgeCategoryRequest,
} from '@api/types/badgeCategory'

interface BadgeCategoryFormDialogProps {
  open: boolean
  onClose: () => void
  category?: BadgeCategoryDto | null
}

export function BadgeCategoryFormDialog({ open, onClose, category }: BadgeCategoryFormDialogProps) {
  const isEditing = !!category
  // The Platform category's isPlatform flag is read-only and immutable.
  const isPlatform = !!category?.isPlatform

  const [name, setName] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [iconName, setIconName] = useState('')
  const [color, setColor] = useState('#1976d2')
  const [sortOrder, setSortOrder] = useState(0)
  const [isActive, setIsActive] = useState(true)

  const [errors, setErrors] = useState<Record<string, string>>({})

  // Emoji picker state
  const [emojiAnchorEl, setEmojiAnchorEl] = useState<HTMLElement | null>(null)
  const emojiPickerOpen = Boolean(emojiAnchorEl)

  const createMutation = useCreateBadgeCategory()
  const updateMutation = useUpdateBadgeCategory()
  const mobileDialogProps = useMobileDialogProps()

  const isPending = createMutation.isPending || updateMutation.isPending

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      if (category) {
        setName(category.name)
        setDisplayName(category.displayName)
        setIconName(category.iconName || '')
        setColor(category.color || '#1976d2')
        setSortOrder(category.sortOrder)
        setIsActive(category.isActive)
      } else {
        setName('')
        setDisplayName('')
        setIconName('')
        setColor('#1976d2')
        setSortOrder(0)
        setIsActive(true)
      }
      setErrors({})
    }
  }, [open, category])

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!name.trim()) {
      newErrors.name = 'Name is required'
    } else if (name.length > 50) {
      newErrors.name = 'Name must be less than 50 characters'
    }

    if (!displayName.trim()) {
      newErrors.displayName = 'Display name is required'
    } else if (displayName.length > 100) {
      newErrors.displayName = 'Display name must be less than 100 characters'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validate()) return

    try {
      if (isEditing && category) {
        const data: UpdateBadgeCategoryRequest = {
          name: name.trim(),
          displayName: displayName.trim(),
          iconName: iconName.trim() || null,
          color: color || null,
          sortOrder,
          isActive,
        }
        await updateMutation.mutateAsync({ id: category.id, data })
      } else {
        const data: CreateBadgeCategoryRequest = {
          name: name.trim(),
          displayName: displayName.trim(),
          iconName: iconName.trim() || null,
          color: color || null,
          sortOrder,
          isActive,
        }
        await createMutation.mutateAsync(data)
      }
      onClose()
    } catch (err) {
      setErrors(prev => ({
        ...prev,
        submit: err instanceof Error ? err.message : 'Failed to save badge category',
      }))
    }
  }

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    setIconName(emojiData.emoji)
    setEmojiAnchorEl(null)
  }

  const handleClearEmoji = () => {
    setIconName('')
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth {...mobileDialogProps}>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            {isEditing ? 'Edit Badge Category' : 'Create Badge Category'}
          </Typography>
          <IconButton onClick={onClose} size="small" disabled={isPending}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {errors.submit && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setErrors(prev => ({ ...prev, submit: '' }))}>
            {errors.submit}
          </Alert>
        )}

        {isPlatform && (
          <Alert severity="info" icon={<LockIcon />} sx={{ mb: 3 }}>
            This is the <strong>Platform</strong> category for milestone badges. The Platform flag is
            system-managed and cannot be changed, and this category cannot be deleted.
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Basic Info */}
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Basic Information
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Name"
                  required
                  fullWidth
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  error={!!errors.name}
                  helperText={errors.name || 'Internal identifier (e.g., nature)'}
                  disabled={isPending}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Display Name"
                  required
                  fullWidth
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  error={!!errors.displayName}
                  helperText={errors.displayName || 'Shown to users (e.g., Nature)'}
                  disabled={isPending}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Appearance */}
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Appearance
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Icon (Emoji) - Optional
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Paper
                    variant="outlined"
                    onClick={(e) => !isPending && setEmojiAnchorEl(e.currentTarget)}
                    sx={{
                      width: 56,
                      height: 56,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: isPending ? 'default' : 'pointer',
                      fontSize: '2rem',
                      '&:hover': {
                        backgroundColor: isPending ? 'transparent' : 'action.hover',
                      },
                    }}
                  >
                    {iconName || <EmojiIcon color="disabled" />}
                  </Paper>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={(e) => !isPending && setEmojiAnchorEl(e.currentTarget)}
                      disabled={isPending}
                    >
                      {iconName ? 'Change' : 'Select'}
                    </Button>
                    {iconName && (
                      <Button
                        size="small"
                        color="error"
                        onClick={handleClearEmoji}
                        disabled={isPending}
                      >
                        Remove
                      </Button>
                    )}
                  </Box>
                </Box>
                <Popover
                  open={emojiPickerOpen}
                  anchorEl={emojiAnchorEl}
                  onClose={() => setEmojiAnchorEl(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                  <EmojiPicker
                    onEmojiClick={handleEmojiClick}
                    theme={Theme.AUTO}
                    emojiStyle={EmojiStyle.NATIVE}
                    // A fixed 350 overflows a 360px phone once the popover's edge margin is added.
                    width="min(350px, calc(100vw - 32px))"
                    height={400}
                  />
                </Popover>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Color"
                  fullWidth
                  type="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  disabled={isPending}
                  InputProps={{ sx: { height: 56 } }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Sort Order"
                  fullWidth
                  type="number"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(parseInt(e.target.value) || 0)}
                  disabled={isPending}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Status */}
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Status
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    disabled={isPending}
                  />
                }
                label="Active"
              />
              {isEditing && (
                <Chip
                  icon={isPlatform ? <LockIcon /> : undefined}
                  label={isPlatform ? 'Platform (read-only)' : 'Standard'}
                  size="small"
                  color={isPlatform ? 'warning' : 'default'}
                  variant={isPlatform ? 'filled' : 'outlined'}
                />
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isPending || !name.trim() || !displayName.trim()}
          startIcon={isPending ? <CircularProgress size={20} /> : null}
        >
          {isPending ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Category'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
